import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parseMarkdownIntoSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage user story versions
 * Now uses the same pattern as features — markdown sections rendered as accordions
 */
const useUserStoryVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  const userStoryConfig = {
    // Determine if a message contains user stories
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      if (content.includes('No structured user stories found') ||
          content.includes('Please format user stories with proper headers')) {
        return false;
      }
      return content.includes('## US') || content.includes('### US');
    },

    // Parse user stories from content — prefer backend JSON, fall back to markdown parser
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseMarkdownIntoSections(content);
    },

    // Extract version information from a message
    extractVersions: (msg, parsedStories, versionNumber, isPreview = false) => {
      if (!parsedStories || parsedStories.length === 0) {
        return null;
      }
      return {
        id: null, // Will be overwritten by useVersionManagement with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        stories: parsedStories,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  const versionData = useVersionManagementV1(userStoryConfig);

  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      stories: versionData.currentVersion.parsedContent || versionData.currentVersion.stories
    };
  }, [versionData.currentVersion]);

  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useUserStoryVersions;
